import { useState } from 'react';
import { Button } from './ui';

interface ImagePreviewProps {
  src: string;
  alt?: string;
  className?: string;
  onRemove?: () => void;
  onRotate?: () => void;
  onZoom?: () => void;
  showControls?: boolean;
  maxHeight?: string;
  maxWidth?: string;
}

export function ImagePreview({
  src,
  alt = 'Aperçu',
  className = '',
  onRemove,
  onRotate,
  onZoom,
  showControls = true,
  maxHeight = '300px',
  maxWidth = '100%',
}: ImagePreviewProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [rotation, setRotation] = useState(0);

  const handleImageLoad = () => {
    setIsLoading(false);
    setError(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setError(true);
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
    onRotate?.();
  };

  const handleZoom = () => {
    onZoom?.();
  };

  return (
    <div className={`relative inline-block ${className}`}>
      <div
        className="relative overflow-hidden rounded-lg border border-gray-200 bg-gray-50"
        style={{ maxWidth, maxHeight }}
      >
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        )}

        {error ? (
          <div className="flex items-center justify-center h-48 w-48 bg-gray-100 rounded-lg">
            <div className="text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="mt-2 text-sm text-gray-500">Erreur de chargement</p>
            </div>
          </div>
        ) : (
          <img
            src={src}
            alt={alt}
            className="w-full h-auto object-contain transition-transform duration-200"
            style={{
              transform: `rotate(${rotation}deg)`,
              maxHeight,
              maxWidth,
            }}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        )}

        {/* Contrôles */}
        {showControls && !isLoading && !error && (
          <div className="absolute top-2 right-2 flex space-x-1">
            {onZoom && (
              <Button
                size="sm"
                variant="secondary"
                onClick={handleZoom}
                className="h-8 w-8 p-0 bg-white/80 hover:bg-white"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                </svg>
              </Button>
            )}

            {onRotate && (
              <Button
                size="sm"
                variant="secondary"
                onClick={handleRotate}
                className="h-8 w-8 p-0 bg-white/80 hover:bg-white"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </Button>
            )}

            {onRemove && (
              <Button
                size="sm"
                variant="destructive"
                onClick={onRemove}
                className="h-8 w-8 p-0"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </Button>
            )}
          </div>
        )}

        {/* Indicateur de rotation */}
        {rotation !== 0 && (
          <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
            {rotation}°
          </div>
        )}
      </div>
    </div>
  );
}

// Composant pour la galerie d'images
interface ImageGalleryProps {
  images: Array<{
    src: string;
    alt?: string;
    id: string;
  }>;
  onRemove?: (id: string) => void;
  onRotate?: (id: string) => void;
  onZoom?: (id: string) => void;
  className?: string;
}

export function ImageGallery({
  images,
  onRemove,
  onRotate,
  onZoom,
  className = '',
}: ImageGalleryProps) {
  if (images.length === 0) {
    return (
      <div className={`text-center py-8 text-gray-500 ${className}`}>
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <p className="mt-2">Aucune image à afficher</p>
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 ${className}`}>
      {images.map((image) => (
        <ImagePreview
          key={image.id}
          src={image.src}
          alt={image.alt}
          onRemove={() => onRemove?.(image.id)}
          onRotate={() => onRotate?.(image.id)}
          onZoom={() => onZoom?.(image.id)}
        />
      ))}
    </div>
  );
}